import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import type {
  AddonPrice,
  AddonPricing,
  AddonType,
  EnhancedAddon,
  TicketCategory,
} from '~/store/support/types';

export function priceByCurrency(
  map: Record<string, AddonPrice> | undefined,
  currentCurrency: string,
): AddonPrice | null {
  if (!map) return null;
  const code = (currentCurrency || '').trim().toUpperCase();
  if (code && map[code]) return map[code];
  if (map.EUR) return map.EUR;
  const first = Object.values(map)[0];
  return first || null;
}

export function getAddonPricing(
  addonPricing: AddonPricing[],
  addonType: AddonType,
): AddonPricing | null {
  return addonPricing.find((p) => p.type === addonType) || null;
}

export function getEffectiveAddonPrice(
  addonPricing: AddonPricing[],
  addonType: AddonType,
  currentCurrency: string,
): AddonPrice | null {
  const pricing = getAddonPricing(addonPricing, addonType);
  const discounted = priceByCurrency(
    pricing?.discountedPrices,
    currentCurrency,
  );
  return discounted || priceByCurrency(pricing?.prices, currentCurrency);
}

export function getAddonDiscountMeta(
  addonPricing: AddonPricing[],
  addonType: AddonType,
): {
  discountReasonKey: string;
  remainingFreeTickets?: number;
} {
  const pricing = getAddonPricing(addonPricing, addonType);
  return {
    discountReasonKey: pricing?.reasonForDiscountedPricesLocoKey || '',
    remainingFreeTickets: pricing?.remainingFreeTickets,
  };
}

export type AddonPriceFormatter = (
  price: AddonPrice | null,
) => string | undefined;

export function buildUpgradeOptions(
  baseOptions: Array<{
    type: AddonType;
    title: string;
    icon: IconDefinition;
  }>,
  category: TicketCategory | null,
  context: {
    addonPricing: AddonPricing[];
    currentCurrency: string;
    uncategorizedCategories: TicketCategory[];
  },
  formatPrice: AddonPriceFormatter,
): EnhancedAddon[] {
  const { addonPricing, currentCurrency, uncategorizedCategories } = context;
  const format = formatPrice;

  const standardOption = baseOptions.find((opt) => opt.type === 'standard');

  const isUncategorized =
    !category || uncategorizedCategories.some((c) => c.id === category.id);

  if (!category || isUncategorized) {
    if (!standardOption) return [];

    const price = getEffectiveAddonPrice(
      addonPricing,
      'standard',
      currentCurrency,
    );
    const meta = getAddonDiscountMeta(addonPricing, 'standard');

    return [
      {
        ...standardOption,
        enabled: true,
        reasonKey: '',
        price: format(price),
        discountReasonKey: meta.discountReasonKey,
        remainingFreeTickets: meta.remainingFreeTickets,
      },
    ];
  }

  const mapped = baseOptions
    .map((option) => {
      const price = getEffectiveAddonPrice(
        addonPricing,
        option.type,
        currentCurrency,
      );
      const meta = getAddonDiscountMeta(addonPricing, option.type);
      const addon = category.addons.find((a) => a.type === option.type);

      if (option.type === 'standard') {
        return {
          ...option,
          enabled: true,
          reasonKey: '',
          price: format(price),
          discountReasonKey: meta.discountReasonKey,
          remainingFreeTickets: meta.remainingFreeTickets,
        } as EnhancedAddon;
      }

      if (!addon) {
        return {
          ...option,
          enabled: false,
          reasonKey: '',
          price: format(price),
          discountReasonKey: meta.discountReasonKey,
          remainingFreeTickets: meta.remainingFreeTickets,
        } as EnhancedAddon;
      }

      return {
        ...option,
        enabled: addon.enabled,
        reasonKey: addon.reasonKey || '',
        price: format(price),
        discountReasonKey: meta.discountReasonKey,
        remainingFreeTickets: meta.remainingFreeTickets,
      } as EnhancedAddon;
    })
    .filter((option) => {
      if (option.type === 'standard') return true;
      return category.addons.some((addon) => addon.type === option.type);
    });

  return mapped as EnhancedAddon[];
}
